'use client';

import * as React from 'react';
import { Suspense } from 'react';
import dynamic from 'next/dynamic';
import FiltersBar from '@/components/FiltersBar';
import { DEFAULT_FILTERS } from '@/lib/filters';
import SearchBarClient from '@/components/SearchBarClient';
import { theme, styles } from '@/lib/uiTheme';

if (typeof window === 'undefined' && process.env.NODE_ENV === 'development') {
  const startTime = Date.now();
  Promise.resolve().then(() => {
    const renderTime = Date.now() - startTime;
    console.log(`[DEV] /listings server render time: ${renderTime}ms`);
  });
}

const ListingsDataClient = dynamic(() => import('./ListingsDataClient'), {
  ssr: false,
  loading: () => <ListingsShell />,
});

const PAGE_CONTAINER: React.CSSProperties = {
  maxWidth: theme.layout.marketplaceMaxWidth,
  margin: '0 auto',
  padding: `${theme.spacing.lg}px ${theme.spacing.xl}px ${theme.spacing.md}px`,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const PAGE_WRAPPER_MOBILE = `
  @media (max-width: 768px) {
    .listings-page-wrapper {
      min-height: 100dvh !important;
      min-height: 100vh !important;
    }
    .listings-page-container {
      padding: 12px 12px 16px !important;
      flex: 1 !important;
      min-height: 0 !important;
    }
  }
`;

function ListingsShell() {
  return (
    <div style={styles.pageBackground({ minHeight: '100vh' })}>
      <div style={PAGE_CONTAINER}>
        <div style={{ flex: '0 0 auto', zIndex: 10 }}>
          <div
            style={{
              padding: `${theme.spacing.sm}px 0 0`,
              borderBottom: `1px solid ${theme.colors.border}`,
              marginBottom: theme.spacing.md,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div>
              <h1
                style={{
                  margin: '0 0 4px',
                  fontSize: 'clamp(1.35rem, 1.8vw, 1.65rem)',
                  fontWeight: theme.typography.extrabold,
                  color: theme.colors.ink,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                }}
              >
                Browse off-market listings
              </h1>
              <p style={{ margin: 0, fontSize: 13, color: theme.colors.inkMuted, lineHeight: 1.4 }}>
                Search, filter, and open opportunities that match what you are looking for.
              </p>
            </div>
            <div
              style={{
                padding: `0 0 ${theme.spacing.sm}px`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 200, maxWidth: 480 }}>
                <SearchBarClient value="" onChange={() => {}} placeholder="City, ZIP, or address" />
              </div>
              <div style={{ flexShrink: 0 }}>
                <FiltersBar value={DEFAULT_FILTERS} onChange={() => {}} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 auto', minHeight: 0, marginTop: theme.spacing.md }}>
          <div
            style={{
              height: typeof window !== 'undefined' ? window.innerHeight - 65 - 180 : 600,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              ...styles.panelPremium({ padding: 0 }),
            }}
          >
            <div
              role="status"
              aria-live="polite"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.inkMuted,
                fontSize: theme.typography.sm,
                background: theme.colors.surfaceMuted,
              }}
            >
              Loading listings…
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ListingsPage() {
  return (
    <div className="listings-page-wrapper" style={styles.pageBackground({ minHeight: '100vh' })}>
      <style dangerouslySetInnerHTML={{ __html: PAGE_WRAPPER_MOBILE }} />
      <div className="listings-page-container" style={PAGE_CONTAINER}>
        <Suspense fallback={<ListingsShell />}>
          <ListingsDataClient />
        </Suspense>
      </div>
    </div>
  );
}
